import { gql } from '@apollo/client';
import { capitalize, cloneDeep } from 'lodash';

import { EscrowPartyTypeEnum } from 'src/graphql/schema';
import { ContactFragment } from 'src/graphql/actions/contact/contact.fragment';

import { apolloClient as client, apolloCache as cache } from '../apollo-client';

import type { IDepositor, IBeneficiary, IContact } from 'src/graphql/schema';

export const removeContactCache = ({
  partyType,
  partyId,
  contactId,
}: {
  partyType: EscrowPartyTypeEnum;
  partyId: string;
  contactId: string;
}) => {
  const party = capitalize(partyType);
  const cacheData = cloneDeep<IDepositor | IBeneficiary | null>(
    client.readFragment({
      id: `${party}:${partyId}`,
      fragment: gql`
        fragment Remove${party}Contact on ${party} {
          id
          contacts {
            id
          }
        }
      `,
    }),
  );

  if (!cacheData) return;

  const contact = cacheData.contacts.find(({ id }) => id === contactId);

  if (!contact) return;

  cache.modify({
    id: cache.identify(contact),
    fields(_fieldValue, { DELETE }) {
      return DELETE;
    },
  });
};

export const addContactCache = ({
  partyType,
  partyId,
  contact,
}: {
  escrowId: string;
  partyType: EscrowPartyTypeEnum;
  partyId: string;
  contact: Partial<IContact>;
}) => {
  const party = capitalize(partyType);
  const fragmentName = `${party}ContactsFragment`;
  const PartyContactsFragment = gql`
  fragment ${fragmentName} on ${party} {
    contacts {
      ...ContactFragment
    }
  }
  ${ContactFragment}
`;
  const cacheData = cloneDeep<IDepositor | IBeneficiary | null>(
    client.readFragment({
      id: `${party}:${partyId}`,
      fragment: PartyContactsFragment,
      fragmentName,
    }),
  );

  if (!cacheData) return;

  client.writeFragment({
    id: `${party}:${partyId}`,
    fragment: PartyContactsFragment,
    fragmentName,
    data: {
      contacts: [...cacheData.contacts, { ...contact, __typename: 'Contact' }],
    },
  });
};
